import { TooltipProps } from '@mui/material';
import * as React from 'react';
import { Content, Heading, LightTooltip } from './StylovyzeTooltip.styles';

export interface StylovyzeTooltipProps extends Omit<TooltipProps, 'title'> {
	/**
	 * MUI tooltip title - use heading and content props instead for structured content.
	 * @deprecated Use heading and content props for better content structure
	 */
	title?: TooltipProps['title'];
	/**
	 * Add standard padding to tooltip content area.
	 * Use for content-heavy tooltips that need more breathing room.
	 */
	padded?: boolean;
	/**
	 * Tooltip heading text - displays above content with emphasis styling.
	 * Optional - can be used alone or with content prop.
	 */
	heading?: string;
	/**
	 * Tooltip body content - main informational text.
	 * Optional - can be used alone or with heading prop.
	 */
	content?: string;
	/**
	 * Remove external spacing from tooltip positioning.
	 * Useful when tooltip is used inside tables or other compact layouts.
	 */
	removeExternalSpacing?: boolean;
}

/**
 * Generic tooltip component for displaying supplementary information with enhanced content structure and Stylovyze theming.
 *
 * Extends MUI Tooltip with structured content support (heading + content) and custom styling for the Stylovyze design system.
 * Inherits all MUI Tooltip functionality - see MUI documentation for complete prop reference.
 *
 * @example
 * // Basic content tooltip
 * <StylovyzeTooltip content="Simple tooltip text">
 *   <Button>Hover me</Button>
 * </StylovyzeTooltip>
 *
 * @example
 * // Structured content with heading
 * <StylovyzeTooltip
 *   heading="Site Goals"
 *   content="Site goals are measuring tools to help your team accomplish goals..."
 *   padded
 *   arrow
 * >
 *   <InfoIcon />
 * </StylovyzeTooltip>
 *
 * @example
 * // Compact tooltip for table contexts
 * <StylovyzeTooltip
 *   content="Status information"
 *   removeExternalSpacing
 * >
 *   <TableCell>Data</TableCell>
 * </StylovyzeTooltip>
 *
 * @see https://stylovyze.info360-dev.com/?path=/story/ds-basic-inputs-content-tooltips--primary
 * @see https://material-ui.com/components/tooltips/ - for inherited MUI Tooltip props
 */
export const StylovyzeTooltip = ({
	heading,
	content,
	title,
	...otherProps
}: StylovyzeTooltipProps) => {
	// Build structured title content when heading/content are provided
	const titleContent = React.useMemo(() => {
		// Prioritize heading/content over title prop
		if (heading || content) {
			return (
				<>
					{heading && <Heading>{heading}</Heading>}
					{content && <Content>{content}</Content>}
				</>
			);
		}

		// Fall back to title prop if no structured content
		if (title) return title;

		// No content provided
		return undefined;
	}, [heading, content, title]);

	return <LightTooltip {...otherProps} title={titleContent} />;
};

StylovyzeTooltip.displayName = 'StylovyzeTooltip';

export default StylovyzeTooltip;
